import {
  getDatabase, ref, update, get, increment,
} from 'firebase/database';
import { getAuth } from 'firebase/auth';
import {
  all, takeEvery, takeLatest, select, put, call,
} from 'redux-saga/effects';
import moment from 'moment-timezone';
import axios from 'axios';
import actions from './actions';
import { TRACKED_ACTION_TYPES, ACTION_TO_ENTRY } from './catalog';
import resolveAttribution from './attribution';

// Caminho global (admin-only) dos contadores de uso, por profissional responsável.
const USAGE_PATH = 'systemUsage/byProfessional';
// Índice "módulos ligados" (gerado pela cloud function buildModuleEntitlements).
const ENTITLEMENTS_PATH = 'systemUsage/entitlements';
const ROOT_URL = process.env.REACT_APP_CLOUD_FUNCTIONS_ROOT_URL;

// --------------- Tracking (auto-escuta das actions de sucesso) ---------------

function* trackUsageHandler(action) {
  try {
    const entry = ACTION_TO_ENTRY[action.type];
    if (!entry) return;
    if (!getAuth().currentUser) return; // ignora sessões públicas/anônimas

    const state = yield select();
    const {
      actorUid, accountUid, professionalUid, attributedToAccount,
    } = resolveAttribution(state, action, entry);
    if (!professionalUid) return;

    const db = getDatabase();
    const now = Date.now();
    const month = moment(now).format('YYYY-MM');
    const day = moment(now).format('YYYY-MM-DD');
    const base = `${USAGE_PATH}/${professionalUid}`;
    const { feature } = entry;

    const updates = {
      [`${base}/accountUid`]: accountUid || null,
      [`${base}/attributedToAccount`]: !!attributedToAccount,
      [`${base}/lastUsedAt`]: now,
      [`${base}/features/${feature}/total`]: increment(1),
      [`${base}/features/${feature}/lastUsedAt`]: now,
      [`${base}/monthly/${month}/${feature}`]: increment(1),
      [`${base}/daily/${day}/${feature}`]: increment(1),
    };
    if (actorUid) {
      updates[`${base}/actors/${actorUid}/${feature}`] = increment(1);
    }

    yield call(() => update(ref(db), updates));
  } catch (error) {
    // O tracking nunca deve quebrar o fluxo do usuário.
    console.warn('[usage] track error', error);
  }
}

export function* trackUsageRequest() {
  yield takeEvery(TRACKED_ACTION_TYPES, trackUsageHandler);
}

// --------------- Leitura para o dashboard (admin) ---------------

function* fetchUsageFromDB() {
  try {
    yield put({ type: actions.FETCHING_USAGE });
    const db = getDatabase();
    const snapshot = yield call(() => get(ref(db, USAGE_PATH)));
    const val = snapshot.val() || {};
    const list = Object.keys(val).map((uid) => ({ professionalUid: uid, ...val[uid] }));
    yield put({ type: actions.FETCH_USAGE_SUCCESS, payload: list });
  } catch (error) {
    yield put({ type: actions.FETCH_USAGE_ERROR });
  }
}

export function* fetchUsageRequest() {
  yield takeLatest(actions.FETCH_USAGE_REQUEST, fetchUsageFromDB);
}

// Uso de UM profissional (para a saúde do card de pós-vendas).
function* fetchProfessionalUsageFromDB(action) {
  try {
    const uid = action.payload;
    if (!uid) return;
    const db = getDatabase();
    const snapshot = yield call(() => get(ref(db, `${USAGE_PATH}/${uid}`)));
    yield put({
      type: actions.FETCH_PROFESSIONAL_USAGE_SUCCESS,
      payload: { uid, usage: snapshot.val() || null },
    });
  } catch (error) {
    console.warn('[usage] fetch professional error', error);
  }
}

export function* fetchProfessionalUsageRequest() {
  yield takeEvery(actions.FETCH_PROFESSIONAL_USAGE_REQUEST, fetchProfessionalUsageFromDB);
}

// --------------- Entitlements (módulos ligados x uso) ---------------

function* fetchEntitlementsFromDB() {
  try {
    yield put({ type: actions.FETCHING_ENTITLEMENTS });
    const db = getDatabase();
    const snapshot = yield call(() => get(ref(db, ENTITLEMENTS_PATH)));
    yield put({ type: actions.FETCH_ENTITLEMENTS_SUCCESS, payload: snapshot.val() || {} });
  } catch (error) {
    yield put({ type: actions.FETCH_ENTITLEMENTS_ERROR });
  }
}

export function* fetchEntitlementsRequest() {
  yield takeLatest(actions.FETCH_ENTITLEMENTS_REQUEST, fetchEntitlementsFromDB);
}

// --------------- Contas criadas no período (acompanhamento) ---------------

// Cloud function listAccountsActivity: contas titulares criadas no intervalo,
// com o último acesso (Firebase Auth) agregado da conta inteira.
function listAccountsActivity(idToken, { since, until }) {
  return axios.post(
    `${ROOT_URL}/listAccountsActivity`,
    { since, until },
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

// Cloud function countAccountsAppointments: agendamentos (RTDB requests/{uid}) de cada
// conta a partir da data de criação dela — exclui histórico migrado e bloqueios.
const APPOINTMENTS_CHUNK = 25;

function countAccountsAppointments(idToken, accounts) {
  return axios.post(
    `${ROOT_URL}/countAccountsAppointments`,
    { accounts },
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

function* countAppointmentsChunk(idToken, chunk) {
  try {
    const response = yield call(countAccountsAppointments, idToken, chunk);
    yield put({
      type: actions.FETCH_ACCOUNT_APPOINTMENTS_SUCCESS,
      payload: (response.data && response.data.counts) || {},
    });
  } catch (error) {
    console.warn('[usage] count appointments error', error);
    yield put({ type: actions.FETCH_ACCOUNT_APPOINTMENTS_ERROR });
  }
}

// Lotes em paralelo: cada lote chega e preenche a tabela sem esperar os demais.
function* countAppointmentsForAccounts(idToken, accounts) {
  const items = (accounts || [])
    .filter((a) => a.uid && a.createdAt)
    .map((a) => ({ uid: a.uid, since: moment(a.createdAt).format('YYYY-MM-DD') }));
  if (items.length === 0) return;
  yield put({ type: actions.FETCHING_ACCOUNT_APPOINTMENTS });
  const chunks = [];
  for (let i = 0; i < items.length; i += APPOINTMENTS_CHUNK) {
    chunks.push(items.slice(i, i + APPOINTMENTS_CHUNK));
  }
  yield all(chunks.map((chunk) => call(countAppointmentsChunk, idToken, chunk)));
  yield put({ type: actions.FETCH_ACCOUNT_APPOINTMENTS_DONE });
}

function* fetchAccountsFromApi(action) {
  try {
    yield put({ type: actions.FETCHING_ACCOUNTS });
    const { currentUser } = getAuth();
    const idToken = yield call(() => currentUser.getIdToken());
    const response = yield call(listAccountsActivity, idToken, action.payload || {});
    const data = response.data || {};
    yield put({ type: actions.FETCH_ACCOUNTS_SUCCESS, payload: data });
    yield call(countAppointmentsForAccounts, idToken, data.accounts);
  } catch (error) {
    console.warn('[usage] fetch accounts error', error);
    yield put({ type: actions.FETCH_ACCOUNTS_ERROR });
  }
}

export function* fetchAccountsRequest() {
  yield takeLatest(actions.FETCH_ACCOUNTS_REQUEST, fetchAccountsFromApi);
}

export default function* usageSaga() {
  yield all([
    trackUsageRequest(),
    fetchUsageRequest(),
    fetchProfessionalUsageRequest(),
    fetchEntitlementsRequest(),
    fetchAccountsRequest(),
  ]);
}
